import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@modules/auth';
import { fetchCustomers } from '@modules/customer';
import { fetchQuote, fetchQuoteItems } from '@modules/quote';
import { QuoteItemOpMenu } from '@modules/quote/components/QuoteItemOpMenu';
import { useEmployee } from '@modules/master/hooks/useEmployee';
import { useUsergroup } from '@modules/master/hooks/useUsergroup';
import { useUnit } from '@modules/master/hooks/useUnit';
import { fetchLimitTypes, fetchPaymentDates } from '@modules/master';
import {
  createDelivery,
  updateDelivery,
  fetchDelivery,
  type DeliveryCreateInput,
} from '../services/delivery.service';
import type { Customer, TaxCalcMode, QuoteStampFlags, EmbeddedFormConfig, LimitType, PaymentDate } from '@shared/types';
import { OrderDocumentTabs } from '@modules/order/components/OrderDocumentTabs';
import { Button, OrderNoInput, SelectMenu, AiReviewSection } from '@shared/components';
import { nextDocumentBranchNumber, toHalfWidth, findIncompleteLineItemIndex, incompleteLineItemMessage } from '@shared/utils';

/** AIによる変更箇所パネルでフィールド名を日本語表示するためのラベル。 */
const DELIVERY_FIELD_LABELS: Record<string, string> = {
  orderId: '案件',
  quoteId: '関連見積',
  customerId: '納品先',
  title: '件名',
  issueDate: '発行日',
  deliveryDate: '納品日',
  assignedUserId: '担当者',
  chiefEmployerId: '確認者',
  usergroupId: '担当部署',
  lineItems: '明細',
  subtotal: '小計',
  totalgst: '消費税',
  grandtotal: '合計（税込）',
  printNote: '備考（印刷）',
  internalNote: '社内メモ',
};

// ---- スタイル定数 ----
// 見積書作成の基本情報と入力欄のサイズ感を揃える（px-3 py-1.5）
const cls =
  'w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';
const readCls =
  'w-full border border-gray-200 rounded px-2 py-1 text-sm bg-gray-50 text-gray-600 min-h-[28px]';
const LBL =
  'bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600 border-r border-gray-100 whitespace-nowrap align-middle';
const VAL = 'px-2 py-1.5 align-middle';
const ROW = 'border-b border-gray-100';
const TH =
  'py-2 px-3 text-left text-xs font-semibold text-gray-600 border-b border-gray-200';

function yen(n: number): string {
  return n.toLocaleString() + '円';
}

// ---- 型定義 ----
type TaxType = 'standard' | 'reduced' | 'exempt';

// 見積明細と同じ税率（非課税）プルダウンの選択肢・表示ラベル
const TAX_OPTIONS: { value: TaxType; label: string }[] = [
  { value: 'standard', label: '10%' },
  { value: 'reduced', label: '8%' },
  { value: 'exempt', label: '非課税' },
];
const taxLabel = (t: TaxType): string => TAX_OPTIONS.find((o) => o.value === t)?.label ?? '';

/** 入力欄右端に表示する下向きシェブロン（単位・税率の選択ボタン共通。見積明細と統一）。 */
function ChevronDown() {
  return (
    <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </svg>
  );
}

interface LineItem {
  name: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  taxType: TaxType;
  sortOrder: number;
}

// 納品書テーブルのデフォルト表示行数（見積書の MIN_ITEM_ROWS と同じ値に揃える）
const MIN_ITEM_ROWS = 15;

function makeEmptyItem(index: number): LineItem {
  return { name: '', quantity: 0, unit: '', unitPrice: 0, taxType: 'standard', sortOrder: index };
}

function initItems(): LineItem[] {
  return Array.from({ length: MIN_ITEM_ROWS }, (_, i) => makeEmptyItem(i));
}

/** items を最低 MIN_ITEM_ROWS 行まで空行でパディングする（見積書の表示行数に合わせる）。 */
function padItems(items: LineItem[]): LineItem[] {
  if (items.length >= MIN_ITEM_ROWS) return items;
  const fillers = Array.from({ length: MIN_ITEM_ROWS - items.length }, (_, i) =>
    makeEmptyItem(items.length + i),
  );
  return [...items, ...fillers];
}

const isBlankItem = (it: LineItem): boolean =>
  it.name.trim() === '' && it.quantity === 0 && it.unitPrice === 0;

/**
 * 末尾の空白行のみ除去し、中間の空白行は帳票レイアウト用に保持する（見積書と同じ挙動）。
 * これにより継承時に空白行が詰められず、レイアウトがそのまま引き継がれる。
 */
function stripTrailingBlanks(items: LineItem[]): LineItem[] {
  let lastFilled = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (!isBlankItem(items[i])) {
      lastFilled = i;
      break;
    }
  }
  return lastFilled < 0 ? [] : items.slice(0, lastFilled + 1);
}

// ---- 合計計算 ----
function roundByMode(value: number, mode: TaxCalcMode): number {
  if (mode === 'ceil') return Math.ceil(value);
  if (mode === 'round') return Math.round(value);
  return Math.floor(value);
}

interface Totals {
  subtotal: number;
  taxAmount: number;
  exempt: number;
  grandTotal: number;
}

function calcTotals(items: LineItem[], mode: TaxCalcMode): Totals {
  let subtotal = 0;
  let tax = 0;
  let exempt = 0;
  for (const item of items) {
    const s = item.unitPrice * item.quantity;
    subtotal += s;
    if (item.taxType === 'standard') tax += roundByMode(s * 0.1, mode);
    else if (item.taxType === 'reduced') tax += roundByMode(s * 0.08, mode);
    else exempt += s;
  }
  return { subtotal, taxAmount: tax, exempt, grandTotal: subtotal + tax };
}

// ---- Field / SectionBox ----
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

function SectionBox({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
        <p className="text-sm font-semibold text-gray-600">{title}</p>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

// ---- 納品先グリッド ----
interface DeliveryDestProps {
  form: DeliveryFormState;
  upd: (p: Partial<DeliveryFormState>) => void;
  customers: Customer[];
  selectedCustomer: Customer | null;
  limitTypes: LimitType[];
  paymentDates: PaymentDate[];
  onCustomerChange: (id: string) => void;
  inheritable?: boolean;
  inheritMode?: boolean;
  onSwitchToInherit?: () => void;
  onSwitchToNew?: () => void;
}

function DeliveryDest({
  form,
  customers,
  selectedCustomer,
  limitTypes,
  paymentDates,
  onCustomerChange,
  inheritable,
  inheritMode,
  onSwitchToInherit,
  onSwitchToNew,
}: DeliveryDestProps) {
  const c = selectedCustomer;
  // 納品先（顧客）の締め日マスタ・支払日マスタを名称解決して表示する
  const limitName = useMemo(
    () => limitTypes.find((l) => l.id === c?.limitTypeId)?.name ?? '',
    [limitTypes, c?.limitTypeId],
  );
  const payName = useMemo(
    () => paymentDates.find((p) => p.id === c?.paymentDateId)?.name ?? '',
    [paymentDates, c?.paymentDateId],
  );
  // 継承元（見積・受注・編集元）がある場合のみ継承トグルを有効化し、ONのときは納品先を読み取り専用表示にする
  const isInherit = Boolean(inheritable && inheritMode);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-1">
          <span className="text-red-400 text-base leading-none">&#128279;</span>
          <p className="text-sm font-semibold text-gray-600">納品先</p>
        </div>
        {/* 顧客継承=継承元の納品先を継承（デフォルト） / 継承解除=継承データを破棄し全欄を空白にする（販管と同じ配置） */}
        {inheritable && (
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" name="delivery-customer-inherit" checked={isInherit} onChange={onSwitchToInherit} className="accent-blue-600" />
              <span>顧客継承</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="radio" name="delivery-customer-inherit" checked={!isInherit} onChange={onSwitchToNew} className="accent-blue-600" />
              <span>継承解除</span>
            </label>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse table-fixed">
          <colgroup>
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
            <col className="w-[12%]" /><col className="w-[13%]" />
          </colgroup>
          <tbody>
            {/* Row 1: 依頼元名 / フリガナ / 本社ID / 〒 */}
            <tr className={ROW}>
              <td className={LBL}>
                依頼元名 <span className="text-red-500">*</span>
              </td>
              <td className={VAL}>
                {isInherit ? (
                  <div className={readCls}>{c?.name ?? ''}</div>
                ) : (
                  <select
                    value={form.customerId}
                    onChange={(e) => onCustomerChange(e.target.value)}
                    required
                    className={cls}
                  >
                    <option value="">選択してください</option>
                    {customers.map((cu) => (
                      <option key={cu.id} value={cu.id}>
                        {cu.name}
                      </option>
                    ))}
                  </select>
                )}
              </td>
              <td className={LBL}>フリガナ</td>
              <td className={VAL}>
                <div className={readCls}>{c?.nameKana ?? ''}</div>
              </td>
              <td className={LBL}>本社ID</td>
              <td className={VAL}>
                <div className={readCls}>{c?.code ?? ''}</div>
              </td>
              <td className={LBL}>〒</td>
              <td className={VAL}>
                <div className={readCls}>{c?.postalCode ?? ''}</div>
              </td>
            </tr>
            {/* Row 2: 都道府県 / 住所(市区町村) / 住所(番地) / 住所(建物名・フロア) */}
            <tr className={ROW}>
              <td className={LBL}>都道府県</td>
              <td className={VAL}>
                <div className={readCls}>{c?.prefecture ?? ''}</div>
              </td>
              <td className={LBL}>住所（市区町村）</td>
              <td className={VAL}>
                <div className={readCls}>{c?.addr1 ?? ''}</div>
              </td>
              <td className={LBL}>住所（番地）</td>
              <td className={VAL}>
                <div className={readCls}>{c?.addr2 ?? ''}</div>
              </td>
              <td className={LBL}>住所（建物名・フロア）</td>
              <td className={VAL}>
                <div className={readCls}>{c?.addr3 ?? ''}</div>
              </td>
            </tr>
            {/* Row 3: 部署 / 請求締日 / 支払日 / TEL */}
            <tr className={ROW}>
              <td className={LBL}>部署</td>
              <td className={VAL}>
                <div className={readCls}>{c?.department ?? ''}</div>
              </td>
              <td className={LBL}>請求締日</td>
              <td className={VAL}>
                <div className={readCls}>{limitName}</div>
              </td>
              <td className={LBL}>支払日</td>
              <td className={VAL}>
                <div className={readCls}>{payName}</div>
              </td>
              <td className={LBL}>TEL</td>
              <td className={VAL}>
                <div className={readCls}>{c?.phone ?? ''}</div>
              </td>
            </tr>
            {/* Row 4: FAX / 担当者 /（空）/（空） */}
            <tr>
              <td className={LBL}>FAX</td>
              <td className={VAL}>
                <div className={readCls}>{c?.fax ?? ''}</div>
              </td>
              <td className={LBL}>担当者</td>
              <td className={VAL}>
                <div className={readCls}></div>
              </td>
              <td className={LBL}></td>
              <td className={VAL}></td>
              <td className={LBL}></td>
              <td className={VAL}></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- 明細行（見積明細と同一仕様：単位ポップアップ・税率ポップアップ・操作メニュー）----
interface ItemRowProps {
  item: LineItem;
  index: number;
  total: number;
  unitOptions: string[];
  onChange: (index: number, patch: Partial<LineItem>) => void;
  onInsertAbove: (index: number) => void;
  onSwapUp: (index: number) => void;
  onSwapDown: (index: number) => void;
  onDeleteMultiple: (index: number, count: number) => void;
  onDelete: (index: number) => void;
}

const rowInputCls =
  'border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

// 単価は値引き（マイナス）入力を許容する。入力途中の "-" や空文字は 0 とみなす。
function parsePrice(s: string): number {
  return s === '' || s === '-' ? 0 : Number(s);
}

function ItemRow({
  item, index, total, unitOptions, onChange,
  onInsertAbove, onSwapUp, onSwapDown, onDeleteMultiple, onDelete,
}: ItemRowProps) {
  const subtotal = item.unitPrice * item.quantity;
  const [showUnitPicker, setShowUnitPicker] = useState(false);
  const [showTaxPicker, setShowTaxPicker] = useState(false);
  const unitRef = useRef<HTMLDivElement>(null);
  const taxRef = useRef<HTMLDivElement>(null);

  const numVal = (v: number): string => (v === 0 ? '' : String(v));

  // 単価入力の表示文字列（マイナス入力途中の "-" を保持するためローカル管理）。
  // 外部から単価が変わった場合（読込・複製など）のみ表示を同期する。
  const [priceText, setPriceText] = useState<string>(() => numVal(item.unitPrice));
  useEffect(() => {
    if (parsePrice(priceText) !== item.unitPrice) setPriceText(numVal(item.unitPrice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.unitPrice]);

  useEffect(() => {
    if (!showUnitPicker && !showTaxPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (unitRef.current && !unitRef.current.contains(e.target as Node)) setShowUnitPicker(false);
      if (taxRef.current && !taxRef.current.contains(e.target as Node)) setShowTaxPicker(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUnitPicker, showTaxPicker]);

  return (
    <tr className="border-t border-gray-100 hover:bg-gray-50">
      <td className="py-1 px-2">
        <input
          value={item.name}
          onChange={(e) => onChange(index, { name: e.target.value })}
          placeholder=""
          className={rowInputCls + ' w-full'}
        />
      </td>
      <td className="py-1 px-2 w-20">
        <input
          type="text"
          inputMode="numeric"
          value={numVal(item.quantity)}
          onChange={(e) => {
            const raw = toHalfWidth(e.target.value).replace(/[^\d]/g, '');
            onChange(index, { quantity: raw === '' ? 0 : Number(raw) });
          }}
          className={rowInputCls + ' w-full text-right'}
        />
      </td>
      <td className="py-1 px-2 w-20">
        <div ref={unitRef} className="relative">
          <input
            value={item.unit}
            onChange={(e) => onChange(index, { unit: e.target.value })}
            placeholder=""
            className={rowInputCls + ' w-full pr-6'}
          />
          <button
            type="button"
            onClick={() => setShowUnitPicker((v) => !v)}
            title="単位を選択"
            className="absolute inset-y-0 right-1.5 flex items-center text-gray-400 hover:text-blue-600"
          >
            <ChevronDown />
          </button>
          {showUnitPicker && (
            <SelectMenu
              items={unitOptions.map((u) => ({
                key: u,
                label: u,
                onSelect: () => { onChange(index, { unit: u }); setShowUnitPicker(false); },
              }))}
            />
          )}
        </div>
      </td>
      <td className="py-1 px-2 w-28">
        <input
          type="text"
          inputMode="numeric"
          value={priceText}
          onChange={(e) => {
            // 全角→半角に変換し、数字と先頭のマイナスのみ許可（値引き入力に対応）
            const raw = toHalfWidth(e.target.value).replace(/[^\d-]/g, '').replace(/(?!^)-/g, '');
            setPriceText(raw);
            onChange(index, { unitPrice: parsePrice(raw) });
          }}
          className={rowInputCls + ' w-full text-right'}
        />
      </td>
      <td className="py-1 px-2 w-28">
        <div className="border border-gray-300 rounded px-2 py-1 text-sm text-right text-gray-700 tabular-nums bg-gray-50">
          {subtotal !== 0 ? subtotal.toLocaleString() : '—'}
        </div>
      </td>
      <td className="py-1 px-2 w-24">
        <div ref={taxRef} className="relative">
          <div
            onClick={() => setShowTaxPicker((v) => !v)}
            className={rowInputCls + ' w-full pr-6 bg-white cursor-pointer select-none flex items-center'}
          >
            {taxLabel(item.taxType)}
          </div>
          <button
            type="button"
            onClick={() => setShowTaxPicker((v) => !v)}
            title="税率を選択"
            className="absolute inset-y-0 right-1.5 flex items-center text-gray-400 hover:text-blue-600"
          >
            <ChevronDown />
          </button>
          {showTaxPicker && (
            <SelectMenu
              items={TAX_OPTIONS.map((o) => ({
                key: o.value,
                label: o.label,
                onSelect: () => { onChange(index, { taxType: o.value }); setShowTaxPicker(false); },
              }))}
            />
          )}
        </div>
      </td>
      <td className="py-1 px-2 w-14 text-center">
        <QuoteItemOpMenu
          index={index}
          total={total}
          onInsertAbove={() => onInsertAbove(index)}
          onSwapUp={() => onSwapUp(index)}
          onSwapDown={() => onSwapDown(index)}
          onDeleteMultiple={(count) => onDeleteMultiple(index, count)}
          onDeleteRow={() => onDelete(index)}
        />
      </td>
    </tr>
  );
}

// ---- 明細テーブル ----
interface ItemsTableProps {
  items: LineItem[];
  setItems: (items: LineItem[]) => void;
  taxCalcMode: TaxCalcMode;
  onTaxCalcModeChange: (mode: TaxCalcMode) => void;
}

function ItemsTable({ items, setItems, taxCalcMode, onTaxCalcModeChange }: ItemsTableProps) {
  // 単位マスタ（マスター管理 → 単位）を読み込み、明細の単位候補に反映する（見積明細と同一）
  const { units, loadUnits } = useUnit();
  useEffect(() => { void loadUnits(); }, [loadUnits]);
  const unitOptions = useMemo(
    () => [...units].sort((a, b) => a.priority - b.priority).map((u) => u.name),
    [units],
  );

  const reindex = (arr: LineItem[]) => arr.map((it, i) => ({ ...it, sortOrder: i }));
  const handleChange = (index: number, patch: Partial<LineItem>) => {
    setItems(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  };
  const handleDelete = (index: number) => {
    setItems(reindex(items.filter((_, i) => i !== index)));
  };
  // 1行上に空行を挿入
  const insertAbove = (index: number) => {
    const next = [...items];
    next.splice(index, 0, makeEmptyItem(0));
    setItems(reindex(next));
  };
  // 上の行・下の行と並び順を入れ替え
  const swap = (a: number, b: number) => {
    if (a < 0 || b < 0 || a >= items.length || b >= items.length) return;
    const next = [...items];
    [next[a], next[b]] = [next[b], next[a]];
    setItems(reindex(next));
  };
  // 指定行を含む count 行を削除
  const deleteMultiple = (index: number, count: number) => {
    const next = [...items];
    next.splice(index, Math.max(1, count));
    setItems(reindex(next));
  };
  const addRow = () =>
    setItems([...items, makeEmptyItem(items.length)]);

  const { subtotal, taxAmount, exempt, grandTotal } = useMemo(
    () => calcTotals(items, taxCalcMode),
    [items, taxCalcMode],
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
        <p className="text-sm font-semibold text-gray-600">納品明細</p>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className={TH}>品名・摘要</th>
              <th className={TH + ' w-24'}>数量</th>
              <th className={TH + ' w-20'}>単位</th>
              <th className={TH + ' w-32'}>単価</th>
              <th className={TH + ' w-32'}>税抜料金</th>
              <th className={TH + ' w-28'}>非課税</th>
              <th className="py-2 px-3 w-12 border-b border-gray-200"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <ItemRow
                key={i}
                item={item}
                index={i}
                total={items.length}
                unitOptions={unitOptions}
                onChange={handleChange}
                onInsertAbove={insertAbove}
                onSwapUp={(idx) => swap(idx, idx - 1)}
                onSwapDown={(idx) => swap(idx, idx + 1)}
                onDeleteMultiple={deleteMultiple}
                onDelete={handleDelete}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-200 bg-white">
        <div className="flex justify-between px-4 py-2 border-b border-gray-100">
          <span className="text-sm text-gray-700">小計</span>
          <span className="text-sm text-gray-900 tabular-nums">{yen(subtotal)}</span>
        </div>
        <div className="flex justify-between px-4 py-2 border-b border-gray-100 items-center">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-gray-700">消費税額</span>
            <select
              value={taxCalcMode}
              onChange={(e) => onTaxCalcModeChange(e.target.value as TaxCalcMode)}
              className="text-xs border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="floor">切り捨て</option>
              <option value="ceil">切り上げ</option>
              <option value="round">四捨五入</option>
              <option value="manual">手動</option>
            </select>
          </div>
          <span className="text-sm text-gray-900 tabular-nums">{yen(taxAmount)}</span>
        </div>
        <div className="flex justify-between px-4 py-2 border-b border-gray-100">
          <span className="text-sm text-gray-700">非課税税額</span>
          <span className="text-sm text-gray-900 tabular-nums">{yen(exempt)}</span>
        </div>
        <div className="flex justify-between px-4 py-2">
          <span className="text-sm font-bold text-gray-900">合計</span>
          <span className="text-sm font-bold text-gray-900 tabular-nums">{yen(grandTotal)}</span>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-gray-200">
        <Button size="sm" onClick={addRow}>
          ADD
        </Button>
      </div>
    </div>
  );
}

// ---- フォーム状態 ----
interface DeliveryFormState {
  orderId: string;
  customerId: string;
  title: string;
  assignedUserId: string;
  chiefEmployerId: string;
  usergroupId: string;
  issueDate: string;
  deliveryDate: string;
  taxCalcMode: TaxCalcMode;
  stampFlags: QuoteStampFlags;
  printNote: string;
  internalNote: string;
}

function todayStr(): string {
  return new Date().toISOString().split('T')[0];
}

const DEFAULT_STATE: DeliveryFormState = {
  orderId: '',
  customerId: '',
  title: '',
  assignedUserId: '',
  chiefEmployerId: '',
  usergroupId: '',
  issueDate: todayStr(),
  deliveryDate: '',
  taxCalcMode: 'floor',
  stampFlags: { tantou: false, kakunin: false, kaisha: false },
  printNote: '',
  internalNote: '',
};

// ---- ページ本体 ----
export function DeliveryFormPage({ embedded }: { embedded?: EmbeddedFormConfig } = {}) {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { employees, loadEmployees } = useEmployee();
  const { usergroups, loadUsergroups } = useUsergroup();
  // 埋め込み表示時は ID・state・遷移をすべて embedded から取得する
  const id = embedded ? embedded.id : params.id;
  const navState = embedded ? embedded.state : location.state;
  const isEdit = Boolean(id);

  const [form, setForm] = useState<DeliveryFormState>(DEFAULT_STATE);
  const [items, setItems] = useState<LineItem[]>(initItems);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customerId) ?? null,
    [customers, form.customerId],
  );
  // 納品先の請求締日・支払日を名称表示するためのマスタ
  const [limitTypes, setLimitTypes] = useState<LimitType[]>([]);
  const [paymentDates, setPaymentDates] = useState<PaymentDate[]>([]);
  // 納品先の継承トグル: 継承元の顧客ID（見積継承・受注・編集元）と継承ON/OFF
  const [inheritedCustomerId, setInheritedCustomerId] = useState<string>('');
  const [inheritMode, setInheritMode] = useState<boolean>(true);
  const [sourceQuoteId, setSourceQuoteId] = useState<string | null>(null);
  const [sourceQuoteNo, setSourceQuoteNo] = useState<string | null>(null);
  const [docNo, setDocNo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 編集対象が「AIレビュー待ち」か（form は DeliveryFormState のためレコードの印は別stateで保持）
  const [aiPending, setAiPending] = useState(false);

  const upd = useCallback((patch: Partial<DeliveryFormState>) => {
    setForm((prev) => ({ ...prev, ...patch }));
  }, []);

  useEffect(() => {
    void fetchCustomers().then(setCustomers);
    void fetchLimitTypes().then(setLimitTypes).catch(() => setLimitTypes([]));
    void fetchPaymentDates().then(setPaymentDates).catch(() => setPaymentDates([]));
    void loadEmployees();
    void loadUsergroups();
  }, [loadEmployees, loadUsergroups]);

  // 関連見積書（継承元）の番号を表示するため、見積番号（枝番）を取得する
  useEffect(() => {
    if (!sourceQuoteId) { setSourceQuoteNo(null); return; }
    let active = true;
    void fetchQuote(sourceQuoteId)
      .then((q) => {
        if (active && q) setSourceQuoteNo(q.code);
        else if (active) setSourceQuoteNo(null);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : '関連文書の取得に失敗しました。');
      });
    return () => { active = false; };
  }, [sourceQuoteId]);

  // 案件詳細から遷移した場合は受注ID・依頼元を初期表示する
  useEffect(() => {
    if (id) return;
    const st = navState as { orderId?: string; customerId?: string } | null;
    if (!st) return;
    setForm((prev) => ({
      ...prev,
      orderId: st.orderId ?? prev.orderId,
      customerId: st.customerId ?? prev.customerId,
    }));
    if (st.customerId) setInheritedCustomerId(st.customerId);
  }, [id, navState]);

  // 見積書から「納品書継承」で遷移した場合、見積の内容を全項目引き継ぐ
  useEffect(() => {
    if (id) return;
    const st = navState as { quoteId?: string; orderId?: string } | null;
    const quoteId = st?.quoteId;
    if (!quoteId) return;
    setLoading(true);
    void (async () => {
      try {
        const [quote, quoteItems] = await Promise.all([
          fetchQuote(quoteId),
          fetchQuoteItems(quoteId),
        ]);
        if (!quote) {
          setError('継承元の見積書が見つかりませんでした');
          return;
        }
        setSourceQuoteId(quoteId);
        setInheritedCustomerId(quote.customerId);
        setForm((prev) => ({
          ...prev,
          orderId: st?.orderId ?? quote.orderId ?? prev.orderId,
          customerId: quote.customerId,
          // Mr.販管仕様: 見積から件名・担当者・確認者・捺印・担当部署を継承。納品日は空欄。
          title: quote.title ?? prev.title,
          assignedUserId: quote.assignedUserId ?? prev.assignedUserId,
          chiefEmployerId: quote.chiefEmployerId ?? '',
          usergroupId: quote.usergroupId ?? '',
          deliveryDate: '',
          taxCalcMode: quote.taxCalcMode ?? prev.taxCalcMode,
          stampFlags: quote.stampFlags ?? prev.stampFlags,
          printNote: quote.notes ?? '',
          internalNote: quote.internalNotes ?? '',
        }));
        if (quoteItems.length > 0) {
          setItems(
            padItems(
              quoteItems.map((it, idx) => ({
                name: it.name,
                quantity: it.quantity,
                unit: it.unit ?? '',
                unitPrice: it.unitPrice,
                taxType: it.taxType,
                sortOrder: idx,
              })),
            ),
          );
        }
      } catch {
        setError('見積書の継承に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navState]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    void fetchDelivery(id)
      .then((d) => {
        if (!d) return;
        setDocNo(d.code ?? null);
        setAiPending(d.aiPendingReview === true);
        setSourceQuoteId(d.quoteId ?? d.sourceDocId ?? null);
        setInheritedCustomerId(d.customerId);
        setForm({
          orderId: d.orderId ?? '',
          customerId: d.customerId,
          title: d.title ?? '',
          assignedUserId: d.assignedUserId ?? '',
          chiefEmployerId: d.chiefEmployerId ?? '',
          usergroupId: d.usergroupId ?? '',
          issueDate: d.issueDate.toDate().toISOString().split('T')[0],
          deliveryDate: d.deliveryDate ? d.deliveryDate.toDate().toISOString().split('T')[0] : '',
          taxCalcMode: d.taxCalcMode ?? 'floor',
          stampFlags: d.stampFlags ?? { tantou: false, kakunin: false, kaisha: false },
          printNote: d.printNote ?? '',
          internalNote: d.internalNote ?? '',
        });
        if (d.lineItems && d.lineItems.length > 0) {
          const loaded: LineItem[] = d.lineItems.map((li, idx) => ({
            name: li.title,
            quantity: li.qty,
            unit: li.unitId,
            unitPrice: li.unitPrice,
            taxType:
              li.taxRate === 10 ? 'standard' : li.taxRate === 8 ? 'reduced' : 'exempt',
            sortOrder: idx,
          }));
          setItems(padItems(loaded));
        }
      })
      .catch(() => setError('納品書の読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, [id]);

  // 新規作成（案件紐付けあり・見積継承含む）の場合、採番予定の枝番をプレビュー表示する
  useEffect(() => {
    if (id) return;
    if (!form.orderId) { setDocNo(null); return; }
    let active = true;
    void nextDocumentBranchNumber('deliveries', form.orderId)
      .then((n) => { if (active) setDocNo(n); })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : '関連文書の取得に失敗しました。');
      });
    return () => { active = false; };
  }, [id, form.orderId]);

  const handleCustomerChange = useCallback(
    (customerId: string) => {
      upd({ customerId });
    },
    [upd],
  );

  // 「継承解除」→「顧客継承」: 継承元の顧客を復元する
  const switchToInherit = useCallback(() => {
    setInheritMode(true);
    if (inheritedCustomerId) upd({ customerId: inheritedCustomerId });
  }, [inheritedCustomerId, upd]);

  // 「顧客継承」→「継承解除」: 継承された納品先を破棄し全欄を空白にする
  const switchToNew = useCallback(() => {
    setInheritMode(false);
    upd({ customerId: '' });
  }, [upd]);

  const handleSubmit = async () => {
    if (!user) { setError('ログインが必要です'); return; }
    // 数量・単位・単価のいずれかが入力された行のみ必須チェック（品名のみのフリー記載行は許可）
    const invalidIndex = findIncompleteLineItemIndex(items);
    if (invalidIndex >= 0) { setError(incompleteLineItemMessage(invalidIndex)); return; }
    setError(null);
    setSaving(true);

    const { subtotal, taxAmount, exempt, grandTotal } = calcTotals(items, form.taxCalcMode);

    // 末尾の空白行のみ除去し、中間の空白行は保持する（見積書と同じ挙動・継承で詰めない）
    const lineItems = stripTrailingBlanks(items).map((it, idx) => ({
      id: String(idx),
      title: it.name,
      qty: it.quantity,
      unitId: it.unit,
      unitPrice: it.unitPrice,
      amount: it.unitPrice * it.quantity,
      taxRate: (it.taxType === 'standard' ? 10 : it.taxType === 'reduced' ? 8 : 'exempt') as
        | 10
        | 8
        | 'exempt',
    }));

    const payload: DeliveryCreateInput = {
      orderId: form.orderId,
      quoteId: sourceQuoteId,
      customerId: form.customerId,
      title: form.title,
      issueDate: Timestamp.fromDate(new Date(form.issueDate)),
      deliveryDate: form.deliveryDate ? Timestamp.fromDate(new Date(form.deliveryDate)) : null,
      deliveredAt: null,
      chiefEmployerId: form.chiefEmployerId || null,
      usergroupId: form.usergroupId || null,
      stampFlags: form.stampFlags,
      lineItems,
      subtotal,
      advance: 0,
      totalgst: taxAmount + exempt,
      grandtotal: grandTotal,
      taxCalcMode: form.taxCalcMode,
      printNote: form.printNote || null,
      internalNote: form.internalNote || null,
      sourceDocId: sourceQuoteId,
      isSplit: false,
      splitSeq: 1,
      assignedUserId: form.assignedUserId,
    };

    try {
      if (isEdit && id) {
        await updateDelivery(id, payload, user.id);
        if (embedded) { embedded.onSaved(); return; }
        navigate('/deliveries/' + id);
      } else {
        const created = await createDelivery(payload, user.id);
        if (embedded) { embedded.onSaved(); return; }
        navigate('/deliveries/' + created.id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    if (embedded) { embedded.onCancel(); return; }
    navigate(id ? '/deliveries/' + id : '/deliveries');
  };

  if (loading) return <div className="p-6 text-gray-500">読み込み中...</div>;

  return (
    <div className={embedded ? 'space-y-4' : 'p-6 space-y-4'}>
      {!embedded && (
        <>
          <div className="flex items-baseline gap-8">
            <h1 className="text-xl font-bold text-gray-800">納品書管理</h1>
            {docNo && <span className="text-base font-semibold text-gray-500">#{docNo}</span>}
          </div>

          <nav className="text-xs text-gray-500 flex items-center gap-1">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <span>/</span>
            <Link to="/deliveries" className="hover:text-blue-600">納品書一覧</Link>
            <span>/</span>
            <span className="text-gray-700">{isEdit ? '納品書編集' : '納品書フォーム'}</span>
          </nav>

          {/* 案件に紐づく納品書は、受注一覧の詳細と同じ管理タブを表示する */}
          {form.orderId && <OrderDocumentTabs orderId={form.orderId} activeTab="delivery" />}
        </>
      )}

      {error && (
        <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </p>
      )}

      {/* AIレビュー待ちの納品書: 変更箇所の表示と「レビュー済みにする」（設計書 §3.5） */}
      {isEdit && id && (
        <AiReviewSection
          entityType="delivery"
          collectionName="deliveries"
          entityId={id}
          pending={aiPending}
          userId={user?.id}
          fieldLabels={DELIVERY_FIELD_LABELS}
          onConfirmed={() => setAiPending(false)}
        />
      )}

      <SectionBox title="基本情報">
        {/* 見積書作成の基本情報と同じく2カラムで配置し、入力欄が枠一杯まで伸びないようにする */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <Field label="件名（案件名）">
            <input
              value={form.title}
              onChange={(e) => upd({ title: e.target.value })}
              placeholder="例：Webシステム開発一式"
              className={cls}
            />
          </Field>
          <Field label="受注番号">
            <OrderNoInput
              orderId={form.orderId}
              onChange={(v) => upd({ orderId: v })}
              className={cls}
            />
          </Field>
          <Field label="関連見積書">
            <div className={readCls}>{sourceQuoteNo ?? '—'}</div>
          </Field>
          <Field label="納品担当者" required>
            <select
              value={form.assignedUserId}
              onChange={(e) => upd({ assignedUserId: e.target.value })}
              required
              className={cls}
            >
              <option value="">選択してください</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="担当部署">
            <select
              value={form.usergroupId}
              onChange={(e) => upd({ usergroupId: e.target.value })}
              className={cls}
            >
              <option value="">未選択</option>
              {usergroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </Field>
          <Field label="確認者">
            <select
              value={form.chiefEmployerId}
              onChange={(e) => upd({ chiefEmployerId: e.target.value })}
              className={cls}
            >
              <option value="">未選択</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </Field>
          <Field label="発行日" required>
            <input
              type="date"
              value={form.issueDate}
              onChange={(e) => upd({ issueDate: e.target.value })}
              required
              className={cls}
            />
          </Field>
          <Field label="納品日">
            <input
              type="date"
              value={form.deliveryDate}
              onChange={(e) => upd({ deliveryDate: e.target.value })}
              className={cls}
            />
          </Field>
          <Field label="捺印">
            <div className="flex gap-6 py-0.5">
              {([
                { key: 'tantou', label: '担当者' },
                { key: 'kakunin', label: '確認者' },
                { key: 'kaisha', label: '会社印' },
              ] as const).map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.stampFlags[key]}
                    onChange={() => upd({ stampFlags: { ...form.stampFlags, [key]: !form.stampFlags[key] } })}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
          </Field>
        </div>
      </SectionBox>

      <DeliveryDest
        form={form}
        upd={upd}
        customers={customers}
        selectedCustomer={selectedCustomer}
        limitTypes={limitTypes}
        paymentDates={paymentDates}
        onCustomerChange={handleCustomerChange}
        inheritable={Boolean(inheritedCustomerId)}
        inheritMode={inheritMode}
        onSwitchToInherit={switchToInherit}
        onSwitchToNew={switchToNew}
      />

      <ItemsTable
        items={items}
        setItems={setItems}
        taxCalcMode={form.taxCalcMode}
        onTaxCalcModeChange={(mode) => upd({ taxCalcMode: mode })}
      />

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2">
          <p className="text-sm font-semibold text-gray-600">備考（メモ）</p>
        </div>
        <div className="p-4 space-y-3">
          <Field label="備考（印刷）">
            <textarea
              value={form.printNote}
              onChange={(e) => upd({ printNote: e.target.value })}
              rows={3}
              className={cls}
              placeholder="納品書に印刷される備考"
            />
          </Field>
          <Field label="社内メモ">
            <textarea
              value={form.internalNote}
              onChange={(e) => upd({ internalNote: e.target.value })}
              rows={2}
              className={cls}
              placeholder="社内共有メモ（印刷されません）"
            />
          </Field>
        </div>
      </div>

      <div className="flex justify-center gap-4 py-4">
        <Button variant="secondary" onClick={onCancel}>
          キャンセル
        </Button>
        <Button disabled={saving} onClick={() => void handleSubmit()}>
          {saving ? '保存中...' : '保存'}
        </Button>
      </div>
    </div>
  );
}
